export interface LegalSection {
  heading: string
  body: string[]
  /** Rendered as fine print: legally complete, visually quiet. */
  fine?: boolean
}

export interface LegalDoc {
  slug: string
  title: string
  eyebrow: string
  updated: string
  intro: string
  sections: LegalSection[]
}

const UPDATED = 'September 2026'
const SUPPORT = 'support@gritvuegear.com'

/** Every policy names the brand and the full product scope the same way. */
const WHO =
  'This site and store are operated by GritVue Gear ("GritVue Gear," "GritVue," "we," "us" or "our"). GritVue is the brand; X1 is a product model.'
const SCOPE =
  'This policy applies to every product sold, shipped or included by GritVue Gear ("Products"), including the GritVue X1 and any future models, Clear, Amber and Smoke lenses, replacement lenses, protective hard cases, anti-fog products, USB-C cables, mystery gifts, promotional items and any other accessory, whether purchased or supplied free.'

export const legalDocs: LegalDoc[] = [
  {
    slug: 'shipping',
    title: 'Shipping policy',
    eyebrow: 'Fast, tracked, shipped from the U.S.',
    updated: UPDATED,
    intro:
      'Every GritVue Gear order ships from our U.S. fulfillment network. No overseas transit, no customs paperwork, no guessing where your gear is.',
    sections: [
      {
        heading: 'Who ships your order',
        body: [WHO, SCOPE],
      },
      {
        heading: 'Where orders ship from',
        body: [
          'GritVue Gear operates a network of U.S. warehouses and fulfillment partners, including locations in Fort Worth, Texas; Tampa, Florida; and Los Angeles, California.',
          'We choose the ship-from location for each order at our sole discretion, based on inventory, speed and your address. Items from a single order may ship from different locations and arrive in separate packages. The ship-from location is not guaranteed and may change without notice.',
        ],
      },
      {
        heading: 'Carrier',
        body: [
          'Orders ship USPS, insured and tracked end to end, on single units and multi-packs alike. We may use another national carrier when it is faster or more reliable for your address.',
        ],
      },
      {
        heading: 'Processing time',
        body: [
          'Orders placed before 2:00pm CT on a business day ship the same day. Orders placed after that, or on a weekend or holiday, ship the next business day.',
          'You receive a USPS tracking number by email the moment your label is created.',
        ],
      },
      {
        heading: 'Delivery time',
        body: [
          'Standard U.S. delivery is 3 to 6 business days after dispatch. Alaska, Hawaii and U.S. territories typically take 5 to 9 business days.',
          'Expedited options, when available, are shown at checkout with their own delivery estimate.',
        ],
      },
      {
        heading: 'Shipping cost',
        body: [
          'Standard U.S. shipping is free on every order, including X1 Essential builds, the X1 Complete System, multi-packs, add-ons and offers presented during checkout.',
          'Expedited or upgraded delivery, when offered, is optional and charged at the rate shown at checkout.',
          'This free-shipping offer and shipping rates may change at any time without notice. The shipping terms shown at checkout when you place your order apply to that order.',
          'Prices and shipping amounts shown on product pages and offers are before tax. Taxes, if applicable, are calculated at checkout.',
          'There are no handling fees, surcharges or hidden add-ons.',
        ],
      },
      {
        heading: 'Lost, delayed or damaged in transit',
        body: [
          'If tracking stops moving for more than 5 business days, email us and we will open a carrier claim and ship a replacement. You do not wait on the claim.',
          'If a package arrives damaged, email us and we ship a replacement the same business day.',
        ],
      },
      {
        heading: 'Address changes',
        body: [
          'Email us within 2 hours of ordering and we will update the address before the label is scanned. After dispatch we will work with the carrier to reroute at no cost to you.',
        ],
      },
      {
        heading: 'International',
        body: [
          'We currently ship within the United States. If you are outside the U.S., email us and we will let you know the moment your country is live.',
        ],
      },
      {
        heading: 'Shipping fine print',
        fine: true,
        body: [
          'Processing and delivery times are estimates, not guarantees. GritVue Gear is not liable for delays caused by carriers, weather, holidays, high volume, incorrect addresses supplied by the customer, or events outside our reasonable control.',
          'Title and risk of loss pass to you when the carrier takes possession of the package. We will still help with carrier claims as described above.',
          'Packages marked delivered by the carrier are considered delivered. For packages reported missing after a delivered scan, we may require you to check with neighbors and your local post office and wait 3 business days before a claim is reviewed.',
          'Returns must be sent to the address on the return label we provide, which may differ from the location your order shipped from. Packages sent to any other address may not be accepted or refunded.',
          '"Free shipping" means free standard shipping, by the carrier and method we select, to eligible U.S. destinations under this policy. It does not include expedited or upgraded delivery chosen at checkout, which is charged at the rate shown. Free shipping has no cash value and is not refunded or credited if an order is returned.',
        ],
      },
    ],
  },
  {
    slug: 'returns',
    title: 'Returns & refunds',
    eyebrow: '30 days, return shipping on us',
    updated: UPDATED,
    intro:
      'Try the X1 on a real job. If it is not for you, send it back within 30 days of delivery for a full refund. We pay the return shipping.',
    sections: [
      {
        heading: 'The short version',
        body: [
          '30 days from delivery. Full refund. Free return label. No restocking fee. You do not need to explain yourself.',
          'Used it on the job already? That is fine. That is the point of the trial.',
        ],
      },
      {
        heading: 'How to start a return',
        body: [
          `Email ${SUPPORT} with your order number and the word "return". We reply with a prepaid label, usually within one business day.`,
          'Pack the frame and lenses in any box you have. The original packaging is not required.',
        ],
      },
      {
        heading: 'When you get your money',
        body: [
          'Refunds are issued to your original payment method within 2 business days of the return being scanned by the carrier, not after it reaches us.',
          'Your bank typically posts the refund 3 to 5 business days after that.',
        ],
      },
      {
        heading: 'Multi-packs',
        body: [
          'Returning a multi-pack? Send back what you are not keeping. We refund the difference at the per-unit price of the pack you keep, so you are never charged the single-unit rate as a penalty. This also applies to private multi-system offers presented during checkout.',
        ],
      },
      {
        heading: 'Lifetime upgrades',
        body: [
          'Returning a single X1 bought with a Lifetime upgrade? The full amount you paid, including the upgrade, is refunded, and the Lifetime Replacement Warranty on that unit ends when the return is processed.',
        ],
      },
      {
        heading: 'Exchanges',
        body: [
          'Want a different pack size instead of a refund? Say so in your email and we ship the new pack as soon as the return is scanned. No second shipping charge.',
        ],
      },
      {
        heading: 'What is not covered here',
        body: [
          'Breakage, cracks and scratched lenses are not returns. They are handled under your X1’s replacement coverage: one-year coverage or the Lifetime Replacement Warranty, depending on your order. See the Lifetime Replacement Warranty page.',
        ],
      },
      {
        heading: 'Returns fine print',
        fine: true,
        body: [
          WHO,
          SCOPE,
          'Refunds cover the purchase price of the Products returned. Free items included with an order, such as hard cases, anti-fog, cables and mystery gifts, have no separate cash value and are not refunded separately.',
          'For hygiene reasons, opened anti-fog products do not need to be returned and are not eligible for a separate refund.',
          'Return shipping labels are provided for returns within the 30-day window only. Returns sent to any address other than the one on our label, or without our label, may not be accepted.',
          'We may decline refunds where the pattern of returns indicates resale, abuse of the policy or fraud.',
        ],
      },
    ],
  },
  {
    slug: 'guarantee',
    title: 'Lifetime Replacement Warranty',
    eyebrow: 'Limited warranty · Break it. We replace it.',
    updated: UPDATED,
    intro:
      'Work gear gets destroyed. That is not your fault and it is not your problem. Every GritVue X1 carries coverage: 30-day basic coverage on an X1 Essential (with optional paid protection), one year on an X1 Complete System, or our Lifetime Replacement Warranty on multi-packs and on single X1 orders with a Lifetime upgrade or Lifetime protection. All are limited warranties under the terms below.',
    sections: [
      {
        heading: 'Which coverage your X1 has',
        body: [
          'X1 Essential: 30-day basic coverage from the delivery date is included. You may add 1-Year Breakage Protection or Lifetime Breakage Protection in the same order, at the price shown in the builder. Paid protection is never preselected.',
          '1-Year Breakage Protection on an X1 Essential: replacement coverage for the covered X1 for one year from the delivery date, under these terms. Lifetime Breakage Protection on an X1 Essential: the same coverage as the Lifetime Replacement Warranty described on this page, for as long as you remain the original covered owner.',
          'Standard single X1 Complete System: one-year replacement coverage. It does not include the Lifetime Replacement Warranty.',
          'Single X1 with a Lifetime upgrade: Lifetime Replacement Warranty on that X1. The upgrade may be offered on the product page or as a promotional offer during checkout, at the price shown in that offer.',
          'Multi-packs and multi-system offers: Lifetime Replacement Warranty on every X1 system in the package, whether it is a public bundle or a multi-system offer presented during checkout, unless the offer states otherwise.',
          'The coverage included with your order is the coverage stated in the offer you accepted and confirmed at checkout.',
          'The price you paid for an eligible Lifetime offer does not change the coverage. Every X1 with Lifetime coverage is covered the same way, under the same conditions and exclusions.',
        ],
      },
      {
        heading: 'What is covered',
        body: [
          'A cracked or snapped frame. A broken hinge or temple. A light module that stops working. A port that stops charging. A battery that will not hold a charge. A scratched or cracked Clear, Amber or Smoke lens.',
          'Damage from real work is covered: dropped off a ladder, run over in the shop, crushed in a toolbox, left on a truck bumper. We do not argue about how it happened.',
        ],
      },
      {
        heading: 'How long it lasts',
        body: [
          'Lifetime Replacement Warranty and Lifetime Breakage Protection: for as long as you, the original purchaser, own the X1, with no expiration date.',
          'One-year replacement coverage and 1-Year Breakage Protection: one year from the delivery date.',
          '30-day basic coverage on an X1 Essential: 30 days from the delivery date, for a unit that arrives defective or fails in normal use.',
          'The Lifetime Replacement Warranty is included at no extra charge with eligible multi-pack orders placed while the warranty promotion is active. Coverage granted on an order stays in force for that order even if the promotion or offer later ends.',
          'Either way there is no registration to remember and no receipt hunt. Your order is already in our system.',
        ],
      },
      {
        heading: 'How to claim it',
        body: [
          `Email ${SUPPORT} with your order number and one line about what happened. No forms and no receipt hunt. Your order is already in our system, and we take it from there.`,
          'Most claims are handled the same day they come in.',
        ],
      },
      {
        heading: 'How many times',
        body: [
          'As many times as work breaks it. One replacement per damaged unit, and there is no lifetime cap on legitimate claims. You buy the X1 once.',
        ],
      },
      {
        heading: 'Fine print',
        fine: true,
        body: [
          'Coverage applies to the original purchaser only and is not transferable if the product is resold or given away.',
          'Warrantor: GritVue Gear, reachable at support@gritvuegear.com. Both the one-year replacement coverage and the Lifetime Replacement Warranty are LIMITED WARRANTIES. They are not "full" warranties, because they include the conditions, exclusions, proof requirements, fees and limitations described in these terms.',
          'Lifetime coverage applies to every X1 in an eligible multi-pack or multi-system offer, to a single X1 Complete System bought with an eligible Lifetime upgrade, and to an X1 Essential bought with Lifetime Breakage Protection. A standard single X1 Complete System bought without an upgrade carries one-year replacement coverage from the delivery date, and an X1 Essential bought without paid protection carries 30-day basic coverage, under these same terms. On an X1 Essential, coverage applies to the frame, electronics and the lens or lenses actually purchased in that order. Larger multi-pack orders may receive priority claim handling at GritVue\u2019s discretion.',
          'A Lifetime upgrade, 1-Year Breakage Protection or Lifetime Breakage Protection must be purchased in the same order as the X1 it covers, through an offer presented by GritVue, and covers only the X1 units on that order. It cannot be added to a past order, transferred to another unit or combined across orders unless GritVue expressly offers it in writing.',
          'Private and final Lifetime upgrade prices are promotional offers shown only in the purchase flow where they appear. They may change or end at any time and do not apply to orders already placed. Paying a lower upgrade price does not change the coverage, and paying the normal price does not add coverage beyond these terms.',
          'The replacement unit itself is free. A flat shipping and handling fee per approved replacement claim applies, is disclosed to you when your claim is approved, and is paid by the customer at the time of the claim. GritVue may change this fee for future claims. This fee covers fulfillment only, is not a charge for the product, and is separate from the shipping charged on new orders.',
          'Prescription work by an independent optical professional does not by itself void coverage on the original GritVue frame and electronics. Third-party prescription lenses, and damage directly caused by third-party fitting or modification, are not covered.',
          'Every claim requires proof of damage in the form of a photo of the damaged unit. Replacements may be held until that proof is received.',
          'In cases where the claim history or circumstances warrant additional verification, GritVue may also require the damaged unit to be returned to our facility before a replacement is issued. In those cases return shipping is arranged and paid by the customer. Routine claims are resolved from the photo alone.',
          'Lost, misplaced or stolen units are not covered. This program covers damage and functional failure of a unit you still have, not replacement of units that are no longer in your possession. A police report does not change this.',
          'Replacements are issued per damaged unit, up to the number of units on the original order. Claims for more units than were purchased will be declined.',
          'The program covers use as work eyewear. Intentional destruction, disassembly, unauthorized modification of the electronics, and damage caused by using the product outside its intended purpose are not covered.',
          'We may decline or pause claims where the pattern indicates resale, abuse of the program or fraudulent submissions.',
          'Replacements are provided as the same model where available, or the closest current equivalent if the model has changed. No cash value, and the replacement does not restart or extend the 30-day return window.',
          'GritVue reserves the right to modify this program for future purchases. Changes never reduce coverage on orders already placed.',
          'This program is operated by GritVue Gear and covers the GritVue X1 frame, electronics and the original GritVue Clear, Amber and Smoke lenses supplied with the order. Accessories and consumables, whether included or purchased separately, such as hard cases, anti-fog care kits and other anti-fog products, RX Capability Inserts, USB-C cables, mystery gifts and promotional items, are not covered by the one-year, lifetime or paid protection programs; if one arrives defective, email us within 30 days of delivery and we will replace it.',
          'Replacement of a Product is the sole and exclusive remedy under this program. It does not cover personal injury, property damage, lost wages, downtime or any other loss, which remain subject to the Legal notice & safety disclaimer.',
          'To the extent permitted by law, any implied warranties, including the implied warranties of merchantability and fitness for a particular purpose, are limited in duration to the duration of the applicable written coverage (one year from delivery for one-year coverage). Some states do not allow limitations on how long an implied warranty lasts, or the exclusion or limitation of incidental or consequential damages, so these limitations may not apply to you.',
          'This warranty gives you specific legal rights, and you may also have other rights which vary from state to state. These terms are available on this page before purchase and are linked from every offer that includes coverage.',
        ],
      },
    ],
  },
  {
    slug: 'privacy',
    title: 'Privacy policy',
    eyebrow: 'Your data is not the product',
    updated: UPDATED,
    intro:
      'We collect the minimum needed to ship your order, support it and honor your lifetime replacement. We do not sell your personal information.',
    sections: [
      {
        heading: 'Who we are',
        body: [WHO, `Questions about your data go to ${SUPPORT}.`],
      },
      {
        heading: 'What we collect',
        body: [
          'Order information: name, shipping address, email, phone number and the items you bought.',
          'Payment information is entered on our payment processor and is never stored on our servers. We see only the last four digits and the card type.',
          'Usage information: pages viewed and general device and location data, through cookies and advertising pixels.',
          'Product registration information you choose to give on our activation page: order number, email, how and where you use the X1, your trade, the feature that mattered most, how you heard about us, whether you would recommend it, and whether you wear prescription lenses.',
        ],
      },
      {
        heading: 'Why we collect it',
        body: [
          'To process, ship and support your order, and to verify lifetime replacement claims.',
          'To send order and delivery updates, and marketing messages if you opted in.',
          'To measure and improve our advertising so the right tradespeople see us.',
          'To register your product, support warranty claims, and plan future products and accessories (such as prescription solutions) from registration answers.',
        ],
      },
      {
        heading: 'Who we share it with',
        body: [
          'Service providers that make the business run: our ecommerce platform, payment processor, shipping carriers, email and SMS provider, form and registration tools, our referral program platform, and advertising platforms such as Meta and TikTok.',
          'We never sell your personal information, and we never share your email or phone number with unrelated brands or data brokers.',
        ],
      },
      {
        heading: 'Your choices',
        body: [
          'Unsubscribe from email at the bottom of any message. Reply STOP to any text to end SMS.',
          `Ask us to access, correct or delete your data by emailing ${SUPPORT}. We respond within 30 days.`,
          'You can opt out of advertising cookies through your browser settings or your device ad-tracking controls.',
        ],
      },
      {
        heading: 'Security and retention',
        body: [
          'Data is transmitted over encrypted connections and kept only as long as needed for orders, warranty claims and legal or tax requirements.',
          'We do not knowingly collect information from anyone under 16.',
        ],
      },
    ],
  },
  {
    slug: 'terms',
    title: 'Terms of service',
    eyebrow: 'The rules, written to be read',
    updated: UPDATED,
    intro:
      'By ordering from GritVue Gear you agree to these terms. We have kept them short and in plain English on purpose.',
    sections: [
      {
        heading: 'Who we are and what this covers',
        body: [
          WHO,
          SCOPE,
          'These terms, together with our Shipping policy, Returns & refunds, Lifetime Replacement Warranty terms, Privacy policy and Legal notice & safety disclaimer, form the entire agreement between you and GritVue Gear.',
        ],
      },
      {
        heading: 'Orders and pricing',
        body: [
          'All prices are in U.S. dollars and are shown before tax. Taxes, if applicable, are calculated at checkout.',
          'Prices, discounts, bundles, upgrade offers, reference prices and shipping terms shown on the site may change at any time without notice. The price and terms shown at checkout when you place your order are the ones that apply to that order.',
          'Shipping estimates shown on the site are estimates only, used to help compare offers. Any difference described as "about" or "roughly" is approximate. The final shipping charge shown at checkout controls.',
          'We may correct obvious pricing or typographical errors. If a correction affects your order, we contact you first and you can cancel for a full refund.',
          'Promotional codes may not be combined with each other unless the checkout allows it, and are limited to one use per customer.',
          'Unless expressly stated otherwise, private offers, Lifetime upgrade offers, special bundle pricing and other promotional offers may not be combined with additional discount codes. That includes every Lifetime upgrade offer, every free-shipping offer, every private multi-system and business deployment offer, and referral discounts. Public discount codes apply only to the products they are configured for at checkout (for example, 5OFF applies only to the standard single X1), and does not combine with referral discounts or any other code.',
        ],
      },
      {
        heading: 'Offers, bundles and reference prices',
        body: [
          'Current prices, bundles and reference prices are shown on the product page and at checkout. They are not listed in these terms because they may change at any time.',
          '"Buy X, Get Y Free" bundles: the free units are included at no additional product charge within that bundle price. What each bundle includes (number of systems and lenses, coverage, shipping and any gift) is stated on the offer and confirmed at checkout.',
          'During checkout we may present optional offers, such as a Lifetime upgrade, free shipping on a specific product, or additional X1 systems at special pricing. Their price, coverage and shipping are stated in the offer itself. These offers are optional, are never preselected, replace your current selection rather than adding to it, are available only where and when they are shown, and may change, be withdrawn or not be shown again at any time.',
          'An offer shown to one customer, at one time or in one purchase flow does not entitle any other customer, or the same customer at another time, to the same price or terms.',
        ],
      },
      {
        heading: 'Promotions fine print',
        fine: true,
        body: [
          'Offers, prices, reference prices, sale names and countdowns may change or end at any time. Price changes and new offers do not apply to orders already placed, and we do not refund price differences on past orders.',
          'Every X1 in a multi-pack bundle is an X1 Complete System, including units received at no additional product charge. If part of a bundle is returned, refunds follow the Returns & refunds policy.',
          'X1 Essential and builder add-ons: an X1 Essential includes only the items listed for it in the builder (the frame, one lens of your choice and a USB-C charging cable). Add-ons such as the Full Lens System, hard case, Anti-Fog Care Kit, RX Capability Insert and paid protection are included only when you select and pay for them. Accepting an upgrade to the X1 Complete System in the builder replaces the Essential items it covers; other add-ons you chose stay in your order.',
          'GritVue Crew Rewards (referral program): a referred new customer may receive a discount on an eligible order, and the referrer may earn a reward for each qualified, completed referred order, in the amounts shown on the Crew Rewards page and in the program terms at the time of the referral. Rewards are issued only after the return and fraud review window; refunded, cancelled or fraudulent orders, self-referrals and duplicate accounts do not qualify. Referral discounts do not combine with private offers or other codes. Tracking, codes and payouts are handled by our referral platform under its program terms, and GritVue may change or end the program at any time.',
          'Batch authenticity check: a matching code on the Verify page confirms that the code belongs to an authorized GritVue production batch. It does not identify an individual unit and does not by itself create, extend or change any warranty or return right.',
          'Mystery gifts are promotional items. They have no cash value, cannot be exchanged or chosen, may vary, are available while supplies last, are not covered by the one-year coverage or the Lifetime Replacement Warranty, and are subject to the Legal notice & safety disclaimer.',
        ],
      },
      {
        heading: 'What the X1 is',
        body: [
          'The GritVue X1 is illuminated work eyewear: a work glasses frame with built-in hands-free lighting and interchangeable lenses. The X1 Essential includes one lens; the X1 Complete System and multi-packs include Clear, Amber and Smoke lenses for each X1.',
          'Use common sense on the job and follow your employer or site requirements for any specific protective equipment your work calls for.',
          'Use of every Product is subject to our Legal notice & safety disclaimer, which is part of these terms.',
        ],
      },
      {
        heading: 'Order acceptance',
        fine: true,
        body: [
          'Your order is an offer to buy. We may refuse or cancel any order, including for suspected fraud, resale, pricing errors or stock limits, and will refund any payment in full. We may limit quantities per customer, household or payment method.',
          'Products are intended for personal and business use by the purchaser, not for resale through unauthorized marketplaces.',
        ],
      },
      {
        heading: 'Prescription lenses',
        body: [
          'The X1 is prescription ready. You may take your X1 to an independent, qualified optical professional for evaluation and custom prescription work. Prescription work performed by a third party does not by itself void your replacement coverage on the original GritVue frame and electronics.',
          'The GritVue RX Capability Insert™ is an optional, removable insert designed to sit behind the main X1 lens. It is sold separately, only when you select it, and is not required to buy or use the X1. It includes the insert only: prescription lenses are not included and must be fitted by an independent optical professional. GritVue does not claim any safety or impact certification for an X1 fitted with an RX insert or third-party prescription lenses.',
        ],
      },
      {
        heading: 'Prescription fine print',
        fine: true,
        body: [
          'Any prescription lens work is performed by an optical professional of your choosing, who is an independent third party. GritVue does not perform, supervise, price or warrant that work, and does not guarantee that every prescription can be adapted, that any provider will agree to do the work, or the pricing, optical accuracy or workmanship of third-party lenses. GritVue is not responsible for a provider declining the job, for the fit, quality, tint or coating of lenses they produce, or for any cost you incur with them.',
          'Lens curvature and frame geometry mean certain prescriptions, including high corrections and strong astigmatism, may not be suitable. Confirm suitability with your optician before ordering. An optician declining a prescription is not a product defect and is not covered by the one-year coverage or the Lifetime Replacement Warranty, though the 30-day return policy still applies.',
          'Prescription lenses fitted by a third party are not covered by the one-year coverage or the Lifetime Replacement Warranty. Damage directly caused by third-party fitting or modification is not covered. Coverage otherwise continues to apply to the original GritVue frame, electronics and the original Clear, Amber and Smoke lenses under your applicable warranty.',
        ],
      },
      {
        heading: 'Returns and replacement',
        body: [
          'Our 30-day return policy and our replacement coverage (one-year coverage and the Lifetime Replacement Warranty) are part of these terms. See those pages for how each one works.',
        ],
      },
      {
        heading: 'Intellectual property',
        body: [
          'GritVue, GritVue X1 and our product photography, copy and branding are ours. Please do not copy them for resale or paid advertising.',
        ],
      },
      {
        heading: 'Liability',
        body: [
          'Our liability for any claim related to a product is limited to the amount you paid for it, which is the standard limit for consumer goods of this kind. Nothing here removes rights you have under applicable consumer law.',
        ],
      },
      {
        heading: 'Questions and disputes',
        body: [
          `Email ${SUPPORT} first. We resolve nearly everything the same week. Disputes are handled as described in the Legal notice & safety disclaimer, including informal resolution first and individual arbitration. These terms are governed by the laws of the State of Florida, and any permitted court proceeding will be brought in the state or federal courts located in Hillsborough County, Florida.`,
        ],
      },
    ],
  },
  {
    slug: 'legal-notice',
    title: 'Legal notice & safety disclaimer',
    eyebrow: 'Read before use',
    updated: UPDATED,
    intro:
      'This notice explains what GritVue Gear products are, what they are not, and the terms under which you use them. By purchasing, receiving or using any GritVue Gear product you confirm that you have read, understood and agree to this notice, which forms part of our Terms of service.',
    sections: [
      {
        heading: 'Who we are and what this covers',
        body: [
          WHO,
          SCOPE,
          'Every warning, disclaimer and limitation in this notice applies to all Products, not only the X1. Where a section names the X1, it applies equally to any other GritVue Gear product used in the same way.',
        ],
      },
      {
        heading: 'What the X1 is',
        body: [
          'The GritVue X1 is an illuminated eyewear frame with interchangeable Clear, Amber and Smoke lenses, designed to put hands-free task lighting where you look. It is sold as a general-purpose work-light and eyewear accessory.',
          'Product descriptions, runtimes, brightness figures and photographs describe typical performance under normal conditions and may vary by light mode, battery age, temperature and use.',
        ],
      },
      {
        heading: 'Not personal protective equipment',
        body: [
          'Unless a specific product listing expressly states a certification, the X1 and its lenses are not certified personal protective equipment (PPE) and are not represented as meeting ANSI Z87.1, OSHA, CSA, EN 166 or any other occupational eye-protection or impact standard.',
          'The X1 must not be used as a substitute for safety glasses, goggles, face shields or any eye or face protection that your employer, jobsite, law or the task requires. Where protection is required, wear the required certified equipment.',
        ],
      },
      {
        heading: 'Welding, cutting and hazardous work',
        body: [
          'The X1 and its lenses, including the Smoke lens, provide no protection against welding arc, UV or infrared radiation, lasers, plasma or torch cutting, brazing or grinding sparks. Never use the X1 as, or in place of, a welding helmet, welding lens or shade-rated filter. Doing so can cause serious and permanent eye injury.',
          'Do not use the X1 as protection against chemicals, molten metal, flying debris, high-velocity impact, electrical arc flash, radiation or any other hazard. The user is solely responsible for assessing the hazards of each task and selecting appropriate certified protection.',
          'Do not use the X1 in explosive or flammable atmospheres, including refineries, fuel areas, grain handling or mining environments classified as hazardous locations. The X1 contains a battery and electronics and is not rated intrinsically safe.',
        ],
      },
      {
        heading: 'Driving, machinery and awareness',
        body: [
          'Tinted lenses reduce light reaching the eye. Do not wear the Amber or Smoke lens while driving at night, in low light or anywhere reduced visibility could create risk. Do not point the light at other people’s eyes or at drivers.',
          'Stay aware of your surroundings. The light is a visibility aid only and does not replace adequate work-area lighting, lockout procedures, machine guarding or any safety practice required for the task.',
        ],
      },
      {
        heading: 'Battery and charging',
        body: [
          'The X1 contains a rechargeable lithium battery. Charge only with the supplied cable or a standard USB-C source, on a non-flammable surface, away from heat, water and direct sun. Stop using the product immediately if it swells, overheats, smells, leaks or is damaged.',
          'Do not open, puncture, modify, crush, immerse or incinerate the product. Keep away from children and pets. Dispose of batteries according to local regulations.',
        ],
      },
      {
        heading: 'Anti-fog products',
        body: [
          'GritVue anti-fog is for external use on lenses only. Do not spray or apply it into the eyes, on skin, near the mouth or nose, or on contact lenses. Do not ingest. Keep out of reach of children and pets.',
          'Some people are sensitive or allergic to ingredients in cleaning and anti-fog products. Read the label before use. If you have a history of skin, eye or respiratory sensitivity, test a small amount on the lens and let it dry fully before wearing.',
          'If anti-fog contacts the eyes, rinse with clean water for several minutes. If irritation, redness, rash, swelling, breathing difficulty or any other reaction occurs, stop use and seek medical attention. Use only in a ventilated area and away from heat, sparks and open flame.',
          'GritVue Gear is not responsible for allergic reactions, irritation or sensitivities to anti-fog, lens coatings, frame materials or packaging, or for any use of anti-fog contrary to its label or this notice.',
        ],
      },
      {
        heading: 'Changing lenses and handling',
        body: [
          'Swap lenses slowly and with clean, dry hands, following the product instructions. Do not force a lens into or out of the frame. Lenses and frame edges can pinch fingers, and a cracked or broken lens can have sharp edges that cause cuts.',
          'Stop using any lens or frame that is cracked, chipped, bent or loose, and never try to repair it. Handle broken parts with care, dispose of them safely and request a replacement.',
          'Do not use tools, heat or chemicals on the frame or lenses. GritVue Gear is not responsible for cuts, pinches, scratches or other injuries that occur while changing, cleaning, handling or disposing of lenses, frames or parts.',
        ],
      },
      {
        heading: 'Light exposure and medical conditions',
        body: [
          'Do not stare directly into the LEDs or shine them into anyone’s eyes. If you have photosensitivity, epilepsy, migraines triggered by light, or any eye or skin condition, consult a doctor before use.',
          'Stop wearing any Product that causes discomfort, headaches, dizziness, pressure marks, skin irritation or any allergic reaction, and consult a medical professional.',
        ],
      },
      {
        heading: 'Hard cases, cables, gifts and accessories',
        body: [
          'Hard cases are for storage and transport only and are not protective equipment. USB-C cables must be kept away from water and used only for charging. Stop using any damaged cable.',
          'Mystery gifts and promotional items are provided free, as is, and must be used according to their own labels and instructions. The same disclaimers, limitations and dispute terms in this notice apply to them.',
          'Products contain small parts and are not toys. They are intended for adults 18 and over. Keep packaging, small parts, batteries and anti-fog away from children.',
        ],
      },
      {
        heading: 'Prescription lenses',
        body: [
          'Prescription lenses fitted by a third-party optical professional are the sole responsibility of that provider and the user. GritVue does not make, inspect or warrant third-party lenses and is not responsible for their fit, optical accuracy or protective performance.',
        ],
      },
      {
        heading: 'Modification and misuse',
        body: [
          'Any disassembly, modification, repair by anyone other than GritVue Gear, use of non-GritVue parts, or use outside the intended purpose voids all coverage and shifts all resulting risk to the user.',
          'Exception: having a qualified optical professional evaluate the X1 or fit prescription lenses does not by itself void coverage on the original GritVue frame and electronics. Damage directly caused by that fitting or modification is not covered, and the third-party lenses themselves are not covered.',
        ],
      },
      {
        heading: 'Assumption of risk',
        body: [
          'You use the X1 at your own risk and choose when, where and how to use it. You accept all risks arising from use that is contrary to this notice, to product instructions, to your employer’s or jobsite rules, or to applicable safety law.',
          'If you are unsure whether the X1 is suitable for a task, do not use it for that task and consult your supervisor or a safety professional.',
        ],
      },
      {
        heading: 'Disclaimer of warranties',
        body: [
          'Except for the express limited warranties published on this site (one-year replacement coverage and the Lifetime Replacement Warranty) and the 30-day return policy, and to the fullest extent permitted by law, GritVue products are provided as is, and GritVue disclaims all other warranties, express or implied, including any implied warranty of merchantability, fitness for a particular purpose or non-infringement. Where a product carries a written limited warranty and applicable law does not allow implied warranties to be disclaimed, those implied warranties are limited in duration as stated in the Lifetime Replacement Warranty terms.',
        ],
        fine: true,
      },
      {
        heading: 'Limitation of liability',
        body: [
          'To the fullest extent permitted by law, GritVue Gear, its owners, officers, employees, suppliers, fulfillment partners and affiliates will not be liable for any indirect, incidental, special, consequential, exemplary or punitive damages, or for any injury, illness, allergic reaction, cut, loss or damage arising from misuse, modification, use contrary to this notice or any product label, use as PPE, use in welding or other hazardous work, use in hazardous locations, or use of anti-fog, accessories, gifts or third-party prescription lenses.',
          'To the fullest extent permitted by law, GritVue’s total liability for any claim relating to a product is limited to the amount you paid for that product.',
          'Some jurisdictions do not allow certain exclusions or limitations, so some of the above may not apply to you. Nothing in this notice limits liability that cannot be limited under applicable law.',
        ],
        fine: true,
      },
      {
        heading: 'Indemnity',
        body: [
          'You agree to indemnify and hold harmless GritVue and its owners, officers, employees and affiliates from claims, losses and expenses, including reasonable attorneys’ fees, arising from your misuse of the product, your violation of this notice or the Terms of service, or your provision of the product to others without this notice.',
          'If you purchase X1 units for employees or a crew, you are responsible for providing them with this notice and for determining the PPE required for their work.',
        ],
        fine: true,
      },
      {
        heading: 'Disputes, arbitration and class-action waiver',
        body: [
          `Before filing any claim, you agree to contact ${SUPPORT} and give us 30 days to resolve it informally.`,
          'Any dispute that is not resolved informally will be settled by binding individual arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules, except that either party may bring an individual claim in small-claims court. You and GritVue waive the right to a jury trial and to participate in any class, collective or representative action.',
          'You may opt out of arbitration by emailing us within 30 days of your first purchase with the subject line "Arbitration opt-out." This notice is governed by the laws of the State of Florida, without regard to conflict-of-law rules. Any arbitration hearing, if one is required, will take place in Hillsborough County, Florida, or by video or phone, and any permitted court proceeding will be brought in the state or federal courts located in Hillsborough County, Florida.',
        ],
        fine: true,
      },
      {
        heading: 'Severability and changes',
        body: [
          'If any part of this notice is found unenforceable, the rest remains in effect. We may update this notice at any time; the version posted on this page at the time of your purchase applies to that purchase.',
          'GritVue Gear is not liable for delay or failure to perform caused by events beyond our reasonable control, including natural disasters, carrier disruptions, supply shortages, labor disputes, government action or outages.',
          'Reviews and testimonials reflect individual experiences and are not a guarantee of any particular result.',
        ],
        fine: true,
      },
    ],
  },
  {
    slug: 'contact',
    title: 'Contact us',
    eyebrow: 'A real person answers',
    updated: UPDATED,
    intro:
      'Order help, replacement claims, crew pricing or a question before you buy. Write to us and you get a human, not a ticket bot.',
    sections: [
      {
        heading: 'Email',
        body: [`${SUPPORT}. Answered Monday to Friday, 8:00am to 6:00pm CT, usually within one business day.`],
      },
      {
        heading: 'Replacement claims',
        body: [
          'Send your order number and one line about what broke. No forms to fill out. We take it from there and most claims are handled the same day.',
        ],
      },
      {
        heading: 'Crews, shops and fleets',
        body: [
          'Outfitting more than 30 people, or need an invoice and net terms? Email us with your company name and headcount and we will quote it directly.',
        ],
      },
      {
        heading: 'Where we ship from',
        body: [
          'GritVue Gear ships from U.S. warehouses and fulfillment partners, including Fort Worth, Texas; Tampa, Florida; and Los Angeles, California. The location for each order is chosen by us.',
          'Every order ships insured and tracked. Returns go to the address on the prepaid label we send you, which may be a different location from the one your order shipped from.',
        ],
      },
    ],
  },
]

export function getLegalDoc(slug: string): LegalDoc | undefined {
  return legalDocs.find((doc) => doc.slug === slug)
}
